'use client'

import * as React from 'react'
import { usePollForPromptTemplateChanges } from './use-poll-for-prompt-template-changes'
import { usePromptTemplateRunOnAppLoad } from './use-prompt-template-run-on-app-load'
import { usePromptTemplateSelectFiles } from './use-prompt-template-select-files'
import { useSelectPromptTemplate } from './use-select-prompt-template'
import { useFetchPromptTemplatesOnMatterChange } from './use-fetch-prompt-templates-on-matter-change'

interface PromptTemplatesContext {
  handleSelectFiles: (files: { id: string; fileExtension?: string | null }[]) => void
  handleOpenSelectFilesModal: () => Promise<void>
  handleSelectPromptTemplate: (id: string) => () => Promise<void>
}

const PromptTemplatesContext = React.createContext<PromptTemplatesContext | undefined>(
  undefined
)

export function usePromptTemplates() {
  const context = React.useContext(PromptTemplatesContext)
  if (!context) {
    throw new Error('usePromptTemplates must be used within a PromptTemplatesProvider')
  }
  return context
}

interface PromptTemplatesProviderProps {
  children: React.ReactNode
}

export function PromptTemplatesProvider({ children }: PromptTemplatesProviderProps) {
  useFetchPromptTemplatesOnMatterChange()

  const {
    handleSelectFiles,
    handleOpenSelectFilesModal,
  } = usePromptTemplateSelectFiles()

  const {
    handleSelectPromptTemplate,
  } = useSelectPromptTemplate({ handleOpenSelectFilesModal })

  usePromptTemplateRunOnAppLoad({
    handleOpenSelectFilesModal,
  })

  usePollForPromptTemplateChanges()

  return (
    <PromptTemplatesContext.Provider
      value={{
        handleSelectFiles,
        handleOpenSelectFilesModal,
        handleSelectPromptTemplate,
      }}
    >
      {children}
    </PromptTemplatesContext.Provider>
  )
}
